import React, { useEffect, useMemo, useState } from "react"
import { Col, Container, Modal, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import AdminUsersComponent from "./AdminUsersComponent"
import Settings from "../Settings/Settings"
import AdminOrganizationsComponent from "./AdminOrganizationsComponent"
import AdminExtranjerosHaitianosComponent from "./AdminExtranjerosHaitianosComponent"
import AdminFichadosComponent from "./AdminFichadosComponent"
import AdminDGIIComponent from "./AdminDGIIComponent"

import AdminDeportadosComponent from "./AdminDeportadosComponent"
import AdminComplicesComponent from "./AdminComplicesComponent"
import AdminEstructuraComponent from "./AdminEstructuraComponent"
import AdminDesaparecidosComponent from "./AdminDesaparecidosComponent"
import AdminExtraditadosComponent from "./AdminExtraditadosComponent"
import AdminCeduladosComponent from "./AdminCeduladosComponent"
import AdminLitigioComponent from "./AdminLitigioComponent"
import AdminEmpresasRASADEPComponent from "./AdminEmpresasRASADEPComponent"
import AdminVerificarAntecedentes from "./AdminVerificarAntecedentes"
import AdminDashboardComponent from "./AdminDashboardComponent"
import AdminUsageStatisticsComponent from "./AdminUsageStatisticsComponent"
import AdminActivityLogComponent from "./AdminActivityLogComponent"
import AdminSalariosComponent from "./AdminSalariosComponent"
import AdminVehiculosComponent from "./AdminVehiculosComponent"
import AdminExpedientesComponent from "./AdminExpedientesComponent"
import AdminSystemMonitorComponent from "./AdminSystemMonitorComponent"
import AdminPartesRevisionComponent from "./AdminPartesRevisionComponent"

type AdminSection =
  | "dashboard"
  | "dgii"
  | "identidades"
  | "deportados"
  | "desaparecidos"
  | "extraditados"
  | "fichados"
  | "complices"
  | "extranjerosHaitianos"
  | "estructuras"
  | "litigio"
  | "cedulados"
  | "empresasRASADEP"
  | "salarios"
  | "vehiculos"
  | "expedientes"
  | "companies"
  | "users"
  | "settings"
  | "usageStatistics"
  | "activityLog"
  | "systemMonitor"
  | "partesRevision"

interface AdminMenuItem {
  key: AdminSection
  label: string
  group: "Datos" | "Administracion"
  icon: string
  title?: string
}

interface AdminContainerModalProps {
  toggle: () => void
  cssClass?: string
  title?: string
  initialSection?: AdminSection
  autoOpenCreate?: boolean
  prefillIdentifier?: string
}

function AdminContainerModal(props: AdminContainerModalProps) {
  const { t } = useTranslation("global")
  const [section, setSection] = useState<AdminSection>(props.initialSection ?? "dashboard")
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (props.initialSection) {
      setSection(props.initialSection)
    }
  }, [props.initialSection])

  const menuItems = useMemo<AdminMenuItem[]>(
    () => [
      { key: "dashboard", label: "Dashboard", group: "Datos", icon: "fas fa-chart-pie" },
      { key: "fichados", label: "Registros", group: "Datos", icon: "fas fa-fingerprint" }, {
        key: "estructuras",
        label: "Estructuras Delictivas / Casos",
        group: "Datos",
        icon: "fas fa-project-diagram",
      },
      {
        key: "complices",
        label: "PADEP",
        group: "Datos",
        icon: "fas fa-user-secret",
        title: "Persona Asociada a un Delito Precedente",
      },
      {
        key: "expedientes",
        label: "Procesos Judiciales",
        group: "Datos",
        icon: "fas fa-gavel",
        title: "Audiencias y Partes",
      },
      { key: "dgii", label: "Empresas / RNC", group: "Datos", icon: "fas fa-building" },
      { key: "identidades", label: "Identidades", group: "Datos", icon: "fas fa-id-card" },
      { key: "deportados", label: "Deportados", group: "Datos", icon: "fas fa-plane-departure" },
      { key: "desaparecidos", label: "Desaparecidos", group: "Datos", icon: "fas fa-user-slash" },
      { key: "extraditados", label: "Extraditados", group: "Datos", icon: "fas fa-gavel" },


      { key: "extranjerosHaitianos", label: "Extranjeros", group: "Datos", icon: "fas fa-globe-americas" },

      { key: "litigio", label: "Litigios", group: "Datos", icon: "fas fa-balance-scale" },
      { key: "cedulados", label: "Cedulados", group: "Datos", icon: "fas fa-address-card" },
      { key: "salarios", label: t("Salarios"), group: "Datos", icon: "fas fa-coins" },
      { key: "vehiculos", label: t("Vehículos"), group: "Datos", icon: "fas fa-car" },
      { key: "empresasRASADEP", label: "Empresas / RASADEP", group: "Datos", icon: "fas fa-industry" },
      { key: "companies", label: t("Empresas Afiliadas"), group: "Administracion", icon: "fas fa-sitemap" },
      { key: "users", label: t("Users"), group: "Administracion", icon: "fas fa-users" },
      { key: "settings", label: t("Settings"), group: "Administracion", icon: "fas fa-cog" },
      {
        key: "usageStatistics",
        label: t("Estadísticas de Uso"),
        group: "Administracion",
        icon: "fas fa-chart-line",
      },
      { key: "activityLog", label: t("Activity Log"), group: "Administracion", icon: "fas fa-history" },
      { key: "systemMonitor", label: "Monitor del Sistema", group: "Administracion", icon: "fas fa-server" },
      { key: "partesRevision", label: "Revisión de Cédulas", group: "Administracion", icon: "fas fa-user-check" },
    ],
    [t]
  )

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return menuItems
    return menuItems.filter(item => item.label.toLowerCase().includes(normalizedQuery))
  }, [menuItems, query])

  const dataItems = filteredItems.filter(item => item.group === "Datos")
  const adminItems = filteredItems.filter(item => item.group === "Administracion")

  const renderMenuItem = (item: AdminMenuItem) => {
    const isActive = section === item.key
    return (
      <li
        key={item.key}
        title={item.title}
        className={
          "clickable border rounded-3 px-3 py-2 mb-2 fw-semibold d-flex align-items-center justify-content-between " +
          (isActive ? "bg-primary text-white border-primary" : "bg-white")
        }
        onClick={() => setSection(item.key)}
      >
        <span>
          <i className={`${item.icon} me-2`} />
          {item.label}
        </span>
        {isActive && <i className="fa fa-check-circle" />}
      </li>
    )
  }

  return (
    <Modal
      show={true}
      onHide={props.toggle}
      size={"xl"}
      className={props.cssClass}
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.title}</Modal.Title>

      </Modal.Header>
      <Modal.Body>
        <Container >
          <Row>
            <Col xs={12} md={4} lg={3}>
              <div className="my-4 bg-white shadow-sm border-end rounded-3 px-3 py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Menu Admin</h5>
                  <span className="badge bg-light text-dark">{menuItems.length}</span>
                </div>
                <hr className="text-light bg-light" />

                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Filtrar sección..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />

                <small className="text-uppercase text-muted fw-bold d-block mb-2">Datos</small>
                <ul className="list-unstyled mb-3">
                  {dataItems.map(renderMenuItem)}
                </ul>

                <small className="text-uppercase text-muted fw-bold d-block mb-2">Administración</small>
                <ul className="list-unstyled mb-0">
                  {adminItems.map(renderMenuItem)}
                </ul>

                {filteredItems.length === 0 && (
                  <p className="text-muted small mb-0">No se encontraron secciones para ese filtro.</p>
                )}
              </div>
            </Col>
            <Col xs={12} md={8} lg={9}>
              <div className="mt-4" />
              {section === "users" && <AdminUsersComponent />}

              {section === "extranjerosHaitianos" && (
                <AdminExtranjerosHaitianosComponent />
              )}
              {section === "deportados" && (
                <AdminDeportadosComponent
                  autoOpenCreate={props.autoOpenCreate}
                  prefillIdentifier={props.prefillIdentifier}
                />
              )}
              {section === "dgii" && <AdminDGIIComponent />}

              {section === "complices" && <AdminComplicesComponent />}
              {section === "litigio" && <AdminLitigioComponent />}
              {/* {section === "detenidos" && <AdminDetenidos />} */}
              {/* {section === "centrosDetenidos" && <AdminCentrosDetenidos />} */}
              {section === "fichados" && (
                <AdminFichadosComponent
                  autoOpenCreate={props.autoOpenCreate}
                  prefillIdentifier={props.prefillIdentifier}
                />
              )}
              {section === "estructuras" && <AdminEstructuraComponent />}
              {section === "companies" && <AdminOrganizationsComponent />}

              {section === "desaparecidos" && (
                <AdminDesaparecidosComponent
                  autoOpenCreate={props.autoOpenCreate}
                  prefillIdentifier={props.prefillIdentifier}
                />
              )}
              {section === "extraditados" && (
                <AdminExtraditadosComponent
                  autoOpenCreate={props.autoOpenCreate}
                  prefillIdentifier={props.prefillIdentifier}
                />
              )}

              {section === "cedulados" && <AdminCeduladosComponent />}
              {section === "salarios" && <AdminSalariosComponent />}
              {section === "vehiculos" && <AdminVehiculosComponent />}
              {section === "empresasRASADEP" && <AdminEmpresasRASADEPComponent />}
              {section === "settings" && <Settings />}
              {section === "identidades" && <AdminVerificarAntecedentes />}
              {section === "dashboard" && <AdminDashboardComponent />}
              {section === "usageStatistics" && <AdminUsageStatisticsComponent />}
              {section === "activityLog" && <AdminActivityLogComponent />}
              {section === "expedientes" && <AdminExpedientesComponent />}
              {section === "systemMonitor" && <AdminSystemMonitorComponent />}
              {section === "partesRevision" && <AdminPartesRevisionComponent />}
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  )
}

export default AdminContainerModal
